import { useState } from "react";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { SectionTitle } from "@/components/GlassBits";
import { FileDown, FileText, Calendar } from "lucide-react";

const PERIODS = [
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "90d", label: "Last 90 days" },
  { key: "custom", label: "Custom range" },
];

export default function Reports() {
  const [period, setPeriod] = useState("30d");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");

  const download = async (path, filename) => {
    const params = new URLSearchParams({ period });
    if (period === "custom" && start && end) {
      params.set("start", new Date(start).toISOString());
      params.set("end", new Date(end + "T23:59:59").toISOString());
    }
    const token = localStorage.getItem("ldc_token");
    const url = `${API}${path}?${params.toString()}`;
    try {
      const r = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" });
      if (!r.ok) throw new Error(await r.text());
      const blob = await r.blob();
      const a = document.createElement("a");
      const dl = URL.createObjectURL(blob);
      a.href = dl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dl);
    } catch (e) { toast.error("Download failed"); }
  };

  return (
    <div>
      <SectionTitle eyebrow="Finance" title="Reports & Statements" />

      <div className="ldc-glass p-5 mb-4">
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#fbce18]/80 mb-2">Period</div>
        <div className="flex flex-wrap gap-2 items-center">
          {PERIODS.map((p) => (
            <button key={p.key} onClick={() => setPeriod(p.key)} className={`px-4 py-2 rounded-md text-xs uppercase tracking-[0.16em] inline-flex items-center gap-2 ${period === p.key ? "bg-[#fbce18] text-black" : "border border-white/10 text-white/60 hover:text-white"}`} data-testid={`reports-period-${p.key}`}>
              <Calendar size={12}/> {p.label}
            </button>
          ))}
          {period === "custom" && (
            <div className="flex items-center gap-2 ml-2">
              <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="ldc-input rounded-md px-3 py-2 text-sm" data-testid="reports-start" />
              <span className="text-white/40">→</span>
              <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="ldc-input rounded-md px-3 py-2 text-sm" data-testid="reports-end" />
            </div>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="ldc-glass p-6">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#fbce18]/80">Global</div>
          <h3 className="mt-1 font-display text-xl">All transactions report</h3>
          <p className="mt-2 text-sm text-white/60">Aggregated ledger across every customer, invoice and refund in the selected period.</p>
          <div className="mt-4 flex gap-2">
            <button onClick={() => download("/admin/reports/global.pdf", `global-${period}.pdf`)} className="ldc-btn-primary px-4 py-2 rounded-md text-sm inline-flex items-center gap-2" data-testid="reports-global-pdf"><FileDown size={14}/> Global PDF</button>
            <button onClick={() => download("/admin/reports/global.csv", `global-${period}.csv`)} className="px-4 py-2 rounded-md text-sm border border-white/10 hover:border-[#fbce18]/40 inline-flex items-center gap-2" data-testid="reports-global-csv"><FileText size={14}/> Global CSV</button>
          </div>
        </div>

        <div className="ldc-glass p-6">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#fbce18]/80">Per customer</div>
          <h3 className="mt-1 font-display text-xl">Customer statement</h3>
          <p className="mt-2 text-sm text-white/60">Download any customer's statement (bank-style with running balance).</p>
          <div className="mt-4 space-y-2">
            <input value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value.toLowerCase())} placeholder="customer@example.com" className="ldc-input w-full rounded-md px-3 py-2 text-sm" data-testid="reports-customer-email" />
            <div className="flex gap-2">
              <button
                onClick={() => {
                  if (!customerEmail) return toast.error("Enter customer email");
                  download(`/admin/customers/${encodeURIComponent(customerEmail)}/statement.pdf`, `${customerEmail}-${period}.pdf`);
                }}
                className="ldc-btn-primary px-4 py-2 rounded-md text-sm inline-flex items-center gap-2"
                data-testid="reports-customer-pdf"
              ><FileDown size={14}/> PDF</button>
              <button
                onClick={() => {
                  if (!customerEmail) return toast.error("Enter customer email");
                  download(`/admin/customers/${encodeURIComponent(customerEmail)}/statement.csv`, `${customerEmail}-${period}.csv`);
                }}
                className="px-4 py-2 rounded-md text-sm border border-white/10 hover:border-[#fbce18]/40 inline-flex items-center gap-2"
                data-testid="reports-customer-csv"
              ><FileText size={14}/> CSV</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
